import {
    Checkbox,
    Link,
    MenuButtonTrigger,
    MenuItemButton,
    MenuItemGroup,
} from "@amzn/storm-ui";
import { useMemo, useState, type ReactElement } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import useEncodedFilterStateUrl from "../../../hooks/misc/useEncodedFilterStateUrl";
import type { FeatureFlags } from "../../../store/featureFlags";
import { FeatureFlagsStore } from "../../../store/featureFlags";
import { discoveryFilterStore } from "../../../store/filters";
import {
    DEFAULT_ALL_ITEM,
    DEFAULT_CLEAR_ALL_ITEM,
    DEFAULT_SELECT_ALL_ITEM,
    type Item,
} from "../../../types/common";
import type { DiscoveryFilter } from "../../../types/filters/common";

import {
    StyledMenu,
    StyledPill,
    StyledSelectAllTextButton,
    StyledClearAllTextButton,
    StyledFilterActionsContainer,
    StyledLinkBox,
} from "./discoveryFilterSharedStyles";

const InlineFlex = styled.div`
    display: inline-flex;
    justify-content: space-between;
`;

interface FilterButtonProps {
    filter: DiscoveryFilter;
    fieldSelections?: Item[];
    options?: Item[];
    singleSelect?: boolean;
    onOptionClick: (option: Item) => void;
    loading?: boolean;
}

export interface FilterButtonActionButton {
    label: string;
    featureFlag?: FeatureFlags;
    onClick: () => void;
    external?: boolean;
}

const FilterButton = ({
    filter,
    fieldSelections = [],
    options = [],
    onOptionClick,
    singleSelect = false,
    loading = false,
}: FilterButtonProps): ReactElement => {
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [removeFilter, setFieldSelections, clearFieldSelections] = useStore(
        discoveryFilterStore,
        (state) => [
            state.removeFilter,
            state.setFieldSelections,
            state.clearFieldSelections,
        ],
    );

    const {
        isAdditional,
        label,
        groupConfiguration = {},
        additionalActionButtons = [],
    } = filter;

    const visibleAdditionalActionButtons = additionalActionButtons.filter(
        (e) =>
            e.featureFlag
                ? FeatureFlagsStore.getState()[e.featureFlag as FeatureFlags]
                : true,
    );

    const isSelectionApplicable =
        (options.length > 0 && fieldSelections.length === 0) ||
        options.some((option) =>
            fieldSelections.find(
                (selection) => selection.label === option.label,
            ),
        );

    const areAllOptionsSelected = (): boolean => {
        return fieldSelections.length === options.length;
    };

    const getSelectedValue = (): string => {
        if (fieldSelections.length > 0) {
            if (areAllOptionsSelected() && !singleSelect) {
                return DEFAULT_ALL_ITEM.label;
            }
            return fieldSelections
                .map((selection) => selection.label)
                .join(",");
        }
        if (singleSelect) {
            return "Select option";
        }
        return DEFAULT_ALL_ITEM.label;
    };

    const selectedValue = getSelectedValue();

    const [setEncodedFilterStateUrl] = useEncodedFilterStateUrl();

    const closeMenu = (): void => {
        setIsMenuOpen(false);
    };

    const handleOnOptionClick = (option: Item): void => {
        onOptionClick(option);
        if (singleSelect) {
            closeMenu();
        }
    };

    const toggleMenu = (): void => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleRemove = (): void => {
        removeFilter(filter);
        setEncodedFilterStateUrl();
    };

    const handleClearAllItems = (e) => {
        e.stopPropagation();
        clearFieldSelections(filter.field);
        setEncodedFilterStateUrl();
    };

    const handleSelectAll = (e) => {
        e.stopPropagation();
        setFieldSelections(filter.field, options);
        setEncodedFilterStateUrl();
    };

    const isMultiSelect = !singleSelect;

    const groupsOptions = options.reduce((acc, option) => {
        const group = option.group;
        if (group) {
            if (!acc[group]) {
                acc[group] = [];
            }
            acc[group].push(option);
        }
        return acc;
    }, {});

    const filterGroups = Object.entries(groupConfiguration)
        .map(([label, config]) => {
            return {
                ...config,
                label,
                options: groupsOptions[label] ?? [],
            };
        })
        .filter((e) => !e.disabled)
        .filter((e) =>
            e.featureFlag
                ? FeatureFlagsStore.getState()[e.featureFlag as FeatureFlags]
                : true,
        )
        .sort((a, b) => {
            return (b.priority ?? 0) - (a.priority ?? 0);
        });

    const generateMenuItemButtons = (options: Item[]) => {
        return options.map((option: Item) => {
            const isSelected = !!fieldSelections.find(
                (selection) =>
                    selection.value === option.value &&
                    selection.label === option.label,
            );
            return (
                <MenuItemButton
                    key={option.label}
                    data-testid={`${option.label}-option`}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleOnOptionClick(option);
                    }}
                >
                    {isMultiSelect ? (
                        <Checkbox
                            id={`${option.label}-option`}
                            label={option.label}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOnOptionClick(option);
                            }}
                            checked={isSelected}
                        />
                    ) : (
                        option.label
                    )}
                </MenuItemButton>
            );
        });
    };

    const labelOverride = useMemo(() => {
        const activeGroups = filterGroups
            .filter((e) => e.options.length > 0)
            .map((e) => e.label);
        return activeGroups.length > 0 ? activeGroups.join("/") : label;
    }, [filterGroups, fieldSelections]);

    return (
        <>
            <StyledMenu
                id={`${label}-menu`}
                data-testid={`${label}-menu`}
                onClick={toggleMenu}
                onClose={closeMenu}
                isOpen={isMenuOpen}
                trigger={
                    isAdditional ? (
                        <span>
                            <StyledPill
                                data-testid={`${label}-pill`}
                                prefix={labelOverride}
                                label={selectedValue}
                                onClose={handleRemove}
                                applied={isSelectionApplicable}
                                loading={loading}
                            />
                        </span>
                    ) : (
                        <MenuButtonTrigger data-testid={`${label}-pill`}>
                            <b>{labelOverride}:</b> {selectedValue}
                        </MenuButtonTrigger>
                    )
                }
            >
                {isMultiSelect && (
                    <StyledFilterActionsContainer>
                        <StyledSelectAllTextButton
                            onClick={handleSelectAll}
                            data-testid="select-all-button"
                        >
                            {DEFAULT_SELECT_ALL_ITEM.label}
                        </StyledSelectAllTextButton>
                        <StyledClearAllTextButton
                            onClick={handleClearAllItems}
                            data-testid="clear-all-button"
                        >
                            {DEFAULT_CLEAR_ALL_ITEM.label}
                        </StyledClearAllTextButton>
                    </StyledFilterActionsContainer>
                )}
                {visibleAdditionalActionButtons.length > 0 && (
                    <StyledFilterActionsContainer>
                        {visibleAdditionalActionButtons.map(
                            (button: FilterButtonActionButton) => {
                                return (
                                    <StyledLinkBox>
                                        <Link
                                            size="xsmall"
                                            onClick={() => {
                                                button.onClick();
                                            }}
                                            external={button.external}
                                        >
                                            {button.label}
                                        </Link>
                                    </StyledLinkBox>
                                );
                            },
                        )}
                    </StyledFilterActionsContainer>
                )}
                {filterGroups.length > 0
                    ? filterGroups.map((group) => {
                          return (
                              <MenuItemGroup
                                  key={group.label}
                                  label={
                                      <InlineFlex>
                                          {group.label}
                                          {group.actionButton && (
                                              <Link
                                                  size="xsmall"
                                                  style={{ marginLeft: 10 }}
                                                  onClick={() => {
                                                      if (group.actionButton)
                                                          group.actionButton.onClick();
                                                  }}
                                                  external
                                              >
                                                  {group.actionButton.label}
                                              </Link>
                                          )}
                                      </InlineFlex>
                                  }
                              >
                                  {generateMenuItemButtons(group.options)}
                              </MenuItemGroup>
                          );
                      })
                    : generateMenuItemButtons(options)}
            </StyledMenu>
        </>
    );
};

export default FilterButton;
